import Cookies from 'universal-cookie';
import { fetchOrAddProductsWishlist } from 'Client/redux/productsWishlist/actions';
import { getLoggedInUserData, loginFailed } from 'Client/redux/user/actions';
import { fetchCartData, fetchCartCount } from 'Client/redux/cart/actions';
import { showOverlay, hideOverlay } from 'Client/redux/overlays/actions';
import { OverlayId as LocalOverlayId } from 'Client/components/Overlays/types';
import { BellaDispatch } from 'Client/redux/store';
import { NEVER_SHOW_SHOP_TO_CLUB } from 'Client/hooks/flags/useNeverShowShopToClub';
import { disableShopToClub } from 'Client/redux/configurations';
import { addNotification } from 'Client/redux/notifications/actions';
import { Config, FeatureToggles } from 'Common/appshell_types';
import { removeFromWishlistStoreAction } from 'Client/redux/looksWishlist/actions';
import { setLDPKnownInfoAction } from 'Client/redux/lookDetail/actions';
import {
    CartFlyoutTimeoutKey,
    DisplayActions,
    Event,
    EventNames,
    EventsType,
    GenericActions,
    UpdateLoginSateActions,
    StoreActions,
} from 'Common/appshell_types/customEventTypes';
import { setPDPProductKnownInfoAction } from 'Client/redux/productdetail/actions';
import { BrazeClientInterface } from 'Client/hooks/Braze/BrazeClient';
import { Maybe } from 'Common/types/utility';

export const createCustomEvents = ({
    dispatch,
    config,
    cookies,
    featureToggles,
    braze,
}: {
    dispatch: BellaDispatch;
    config: Config;
    cookies: Cookies;
    featureToggles?: FeatureToggles;
    braze: Maybe<BrazeClientInterface>;
}): Event[] => {
    const { currency, countryCode } = config;
    return [
        {
            name: EventNames.UPDATE_WISHLIST,
            event: (e: EventsType[EventNames.UPDATE_WISHLIST]) => {
                const { action } = e.detail;
                if (action === GenericActions.REFRESH) {
                    dispatch(fetchOrAddProductsWishlist());
                }
            },
        },
        {
            name: EventNames.NOTIFICATION,
            event: (e: EventsType[EventNames.NOTIFICATION]) => {
                const { action, message, autoHideInterval, displayType } = e.detail;
                if (action === DisplayActions.SHOW) {
                    dispatch(addNotification(message, autoHideInterval, displayType));
                }
            },
        },
        {
            name: EventNames.UPDATE_OVERLAYS,
            event: (e: EventsType[EventNames.UPDATE_OVERLAYS]) => {
                const { action, id, options } = e.detail;
                switch (action) {
                    case 'SHOW':
                        return dispatch(showOverlay(id, options));
                    case 'HIDE':
                        return dispatch(hideOverlay(id));
                    default:
                        return null;
                }
            },
        },
        {
            name: EventNames.UPDATE_LOGIN_STATE,
            event: (e: EventsType[EventNames.UPDATE_LOGIN_STATE]) => {
                const { action } = e.detail;
                if (action === UpdateLoginSateActions.LOGIN) {
                    dispatch(getLoggedInUserData());
                } else if (action === UpdateLoginSateActions.EXISTING_USER_LOGIN_FAIL) {
                    dispatch(loginFailed(new Error('remote client user failed to login')));
                    dispatch(disableShopToClub());
                    cookies.set(NEVER_SHOW_SHOP_TO_CLUB, 1, { path: '/' });
                }
            },
        },
        {
            name: EventNames.UPDATE_CART,
            event: async (e: EventsType[EventNames.UPDATE_CART]) => {
                const { action } = e.detail;
                if (action !== GenericActions.REFRESH) {
                    return;
                }

                await Promise.all([dispatch(fetchCartCount()), dispatch(fetchCartData({ countryCode, currency }))]);
                if (!e.detail.shouldShowCartFlyout) {
                    return;
                }
                dispatch(showOverlay(LocalOverlayId.DESKTOP_CART_OVERLAY));
                const timeoutID = window.setTimeout(() => {
                    dispatch(hideOverlay(LocalOverlayId.DESKTOP_CART_OVERLAY));
                }, 2000);
                window[CartFlyoutTimeoutKey] = timeoutID;
            },
        },
        {
            name: EventNames.LOOKS_WISHLIST,
            event: async (e: EventsType[EventNames.LOOKS_WISHLIST]) => {
                const { action, slug } = e.detail;
                if (action === StoreActions.REMOVE) {
                    dispatch(removeFromWishlistStoreAction(slug));
                }
            },
        },
        {
            name: EventNames.LOOK_DETAIL_PAGE,
            event: async (e: EventsType[EventNames.LOOK_DETAIL_PAGE]) => {
                const { action, look } = e.detail;
                if (action === StoreActions.PRE_FILL) {
                    dispatch(setLDPKnownInfoAction(look));
                }
            },
        },
        {
            name: EventNames.PRODUCT_DETAIL_PAGE,
            event: async (e: EventsType[EventNames.PRODUCT_DETAIL_PAGE]) => {
                const { action, product } = e.detail;

                if (action === StoreActions.PRE_FILL) {
                    dispatch(setPDPProductKnownInfoAction(product, !!featureToggles?.p100PdpShowMoodImageFirst));
                }
            },
        },
        {
            name: EventNames.SEND_BRAZE_CUSTOM_EVENT,
            event: (e: EventsType[EventNames.SEND_BRAZE_CUSTOM_EVENT]) => {
                const {
                    action,
                    event: { name, properties = {} },
                } = e.detail;
                if (action === GenericActions.SEND && !!braze) {
                    braze.logCustomEvent(name, properties);
                }
            },
        },
    ];
};
